import { useEffect, useState } from 'react';
import { X, AlertCircle, AlertTriangle } from 'lucide-react';
import apiService from '@/services/api';

interface EscalateJobModalProps {
  jobId: string;
  currentStatus: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (escalationType: string, priorityLevel: string, adminAssignment: string, notes: string) => void;
}

const EscalateJobModal = ({
  jobId,
  currentStatus,
  isOpen,
  onClose,
  onConfirm,
}: EscalateJobModalProps) => {
  const [escalationType, setEscalationType] = useState('');
  const [priorityLevel, setPriorityLevel] = useState('HIGH');
  // (audit 2026-06-09) — list of admin assignees now fetched from real users
  // table. Previously had hardcoded "Marcus Thorne (Senior Lead)" etc.
  const [adminAssignment, setAdminAssignment] = useState('');
  const [adminOptions, setAdminOptions] = useState<string[]>([]);
  const [escalationNotes, setEscalationNotes] = useState('');

  const escalationTypes = [
    'Technical Failure',
    'Quality Issue',
    'Customer Complaint',
    'Payment Problem',
    'Safety Concern',
    'Compliance Issue',
    'Resource Shortage',
    'Other',
  ];

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res: any = await apiService.request('/admin/users?role=admin&limit=50', 'GET');
        if (cancelled) return;
        const users = Array.isArray(res?.data?.users) ? res.data.users
                    : Array.isArray(res?.data) ? res.data : [];
        const names = users
          .map((u: any) => {
            const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email || `User ${u.id}`;
            return name;
          })
          .filter(Boolean);
        setAdminOptions(names);
        if (names.length && !adminAssignment) setAdminAssignment(names[0]);
      } catch (err) {
        // If the lookup fails, leave the list empty — admin can still escalate
        // without an assignee (the field is optional on the backend).
        console.warn('Could not load admin assignees:', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleConfirm = () => {
    if (escalationType && priorityLevel) {
      onConfirm(escalationType, priorityLevel, adminAssignment, escalationNotes);
      handleClose();
    }
  };

  const handleClose = () => {
    setEscalationType('');
    setPriorityLevel('HIGH');
    setAdminAssignment(adminOptions[0] || '');
    setEscalationNotes('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Escalate Job</h2>
              <p className="text-xs uppercase font-bold text-red-600 tracking-wider">Action Protocol Required</p>
            </div>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Job Reference & Status */}
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-600 p-4 rounded">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-1">Reference</p>
                <p className="text-lg font-bold text-gray-900">{jobId}</p>
              </div>
              <div>
                <p className="text-xs uppercase font-bold text-gray-500 tracking-wider mb-1">Current Status</p>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-600"></div>
                  <p className="font-semibold text-gray-900 uppercase">{currentStatus}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Escalation Type */}
          <div>
            <label className="block text-sm font-bold uppercase text-gray-500 tracking-wider mb-3">
              Escalation Type
            </label>
            <select
              value={escalationType}
              onChange={(e) => setEscalationType(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 bg-white"
            >
              <option value="">Select escalation type...</option>
              {escalationTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Priority Level & Admin Assignment Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Priority Level */}
            <div>
              <label className="block text-xs uppercase font-bold text-gray-500 tracking-wider mb-3">
                Priority Level
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => setPriorityLevel('HIGH')}
                  className={`flex-1 py-2 px-3 rounded-lg font-semibold text-sm transition-all ${
                    priorityLevel === 'HIGH'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-600 border border-gray-300'
                  }`}
                >
                  HIGH
                </button>
                <button
                  onClick={() => setPriorityLevel('CRITICAL')}
                  className={`flex-1 py-2 px-3 rounded-lg font-semibold text-sm transition-all ${
                    priorityLevel === 'CRITICAL'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-600 border border-gray-300'
                  }`}
                >
                  CRITICAL
                </button>
              </div>
            </div>

            {/* Admin Assignment */}
            <div>
              <label className="block text-xs uppercase font-bold text-gray-500 tracking-wider mb-3">
                Admin Assignment
              </label>
              <select
                value={adminAssignment}
                onChange={(e) => setAdminAssignment(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 bg-white"
              >
                {adminOptions.map((admin) => (
                  <option key={admin} value={admin}>
                    {admin}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Escalation Notes */}
          <div>
            <label className="block text-xs uppercase font-bold text-gray-500 tracking-wider mb-2">
              Escalation Notes
            </label>
            <textarea
              value={escalationNotes}
              onChange={(e) => setEscalationNotes(e.target.value)}
              placeholder="Provide detailed context for the escalation team..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
              rows={5}
            />
          </div>

          {/* Warning Banner */}
          <div className="flex items-start gap-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-orange-700">Escalation triggers workflow review</p>
              <p className="text-xs text-orange-600 mt-1">All stakeholders will be notified automatically</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={handleClose}
            className="px-6 py-2 text-gray-600 font-semibold hover:text-gray-800"
          >
            Discard
          </button>
          <button
            onClick={handleConfirm}
            disabled={!escalationType || !priorityLevel}
            className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            ! Escalate
          </button>
        </div>
      </div>
    </div>
  );
};

export default EscalateJobModal;
